import { UserRole, Action, Platform } from "@repo/types";
import { Permission, AdminPlatformPermissions } from './permission'

type PermissionsConfig = {
  features: Permission[];
};

type RoleConfig = PermissionsConfig | AdminPlatformPermissions;

// ============================================================================
// PERMISSION SUBJECTS - Define permission subjects/objects
// ============================================================================

/**
 * Permission subjects define the resources or features that can be controlled by permissions.
 */
export const PERMISSION_SUBJECT_FEATURES = {
  ALL: "all",
  MAIN: "main",
  JUNIPER_TRANSACTIONS: "juniper_transactions",
  TRUST_REQUESTS: "trust_requests",
  ANALYTICS: "analytics",
  SUPPORT: "support",
  SETTINGS: "settings",
  REPORTS: "reports",
  FEEDBACK: "feedback",
  AR_COLLECTIONS: 'ar-collections',
  NOTIFICATIONS: 'notifications',
  AR_COLLECTIONS_REPORTING: {
    BASE: 'ar-collections-reporting',
    PERFORMANCE: 'performance',
    EMAIL_QUEUE: 'email-queue',
    APPEALS: 'appeals',
    WRITE_OFF: 'write-off',
    LEDGER_REPORTS: 'ledger-reports',
    ESCALATION_REPORT: 'escalation-report',
  },
  AR_COLLECTIONS_QUEUE: 'ar-collections-queue',
} as const;

// ============================================================================
// PERMISSION TEMPLATES - Define reusable permission templates
// ============================================================================

/**
 * Creates a permission object with a subject and action
 * @param {string} subject - The permission subject (route or feature identifier)
 * @param {Action} action - The action allowed (default: MANAGE)
 * @returns {Permission} Permission object with subject and action
 */
const createPermission = (subject: string, action: Action = Action.MANAGE): Permission => ({
  subject,
  action
});

/**
 * Permission templates for AR Collections features
 * Defines different access levels for AR Collections functionality
 */
export const AR_COLLECTIONS_FEATURES: {
  FULL_ACCESS: Permission[];
  STANDARD_ACCESS: Permission[];
  HR_STANDARD: Permission[];
  WITHOUT_WRITE_OFF: Permission[];
  LEDGER_AND_ATTY_RESPONSE: Permission[];
} = {
  /**
   * Full access: All AR Collections features including Performance
   */
  FULL_ACCESS: [
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.BASE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.EMAIL_QUEUE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.PERFORMANCE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.APPEALS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.WRITE_OFF),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.LEDGER_REPORTS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.ESCALATION_REPORT),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_QUEUE),
  ],

  /**
   * Standard access: All AR Collections features except Performance
   */
  STANDARD_ACCESS: [
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.BASE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.EMAIL_QUEUE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.APPEALS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.LEDGER_REPORTS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_QUEUE),
  ],

  /**
   * HR Standard access: Only Performance feature, no Queue access
   */
  HR_STANDARD: [
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.BASE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.PERFORMANCE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.ESCALATION_REPORT),
  ],
  WITHOUT_WRITE_OFF: [
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.BASE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.PERFORMANCE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.EMAIL_QUEUE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.APPEALS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.LEDGER_REPORTS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.ESCALATION_REPORT),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_QUEUE),
  ],

  /**
   * Attorney Standard access: Only Ledger Reports, no Queue access
   */
  LEDGER_AND_ATTY_RESPONSE: [
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.BASE),
    createPermission(PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.LEDGER_REPORTS),
  ],
};

// ============================================================================
// ROLE CONFIGURATIONS - Define permissions for each role
// ============================================================================

/**
 * Role configurations map each user role to its specific permissions.
 * Permissions are separated into router (navigation) and features (functionality).
 * Note: Not all roles have configurations - only roles with AR Collections access are defined.
 */
const ROLE_CONFIGS: Partial<Record<UserRole, RoleConfig>> = {
  /**
   * Admin role: Full permissions
   * - XTND platform: All routes and features
   * - Lydecker platform: Full AR Collections access with Write-off
   */
  [UserRole.ADMIN]: {
    XTND: {
      features: [createPermission(PERMISSION_SUBJECT_FEATURES.ALL)]
    } as PermissionsConfig,
    LYDECKER: {
      features: AR_COLLECTIONS_FEATURES.FULL_ACCESS
    } as PermissionsConfig
  } as AdminPlatformPermissions,

  /**
   * Clerk: Full AR Collections access including Performance feature
   */
  [UserRole.CLERK]: {
    features: AR_COLLECTIONS_FEATURES.STANDARD_ACCESS
  },

  /**
   * Billing Admin: Full AR Collections routes, standard features (no Performance)
   */
  [UserRole.BILLING_ADMIN]: {
    features: AR_COLLECTIONS_FEATURES.STANDARD_ACCESS
  },

  /**
   * Bookkeeper: Same permissions as Billing Admin
   * Full AR Collections routes, standard features (no Performance)
   */
  [UserRole.BOOKKEEPER]: {
    features: AR_COLLECTIONS_FEATURES.STANDARD_ACCESS
  },

  /**
   * Accounting Admin: Full AR Collections access including Performance feature
   */
  [UserRole.ACCOUNTING_ADMIN]: {
    features: AR_COLLECTIONS_FEATURES.WITHOUT_WRITE_OFF
  },

  /**
   * Supervisor: Full AR Collections access including Performance feature
   */
  [UserRole.SUPERVISOR]: {
    features: AR_COLLECTIONS_FEATURES.WITHOUT_WRITE_OFF
  },

  /**
   * HR Admin: Full AR Collections access including Performance feature
   */
  [UserRole.HR_ADMIN]: {
    features: AR_COLLECTIONS_FEATURES.WITHOUT_WRITE_OFF
  },

  /**
   * HR Standard: No Queue access, only Performance feature
   */
  [UserRole.HR_STANDARD]: {
    features: AR_COLLECTIONS_FEATURES.HR_STANDARD
  },

  /**
   * Partner: Ledger Reports only, no Queue access
   */
  [UserRole.PARTNER]: {
    features: AR_COLLECTIONS_FEATURES.LEDGER_AND_ATTY_RESPONSE
  },

  /**
   * Associate: Ledger Reports only, no Queue access
   */
  [UserRole.ASSOCIATE]: {
    features: AR_COLLECTIONS_FEATURES.LEDGER_AND_ATTY_RESPONSE
  },

  /**
   * Assistant: Ledger Reports only, no Queue access
   */
  [UserRole.ASSISTANT]: {
    features: AR_COLLECTIONS_FEATURES.LEDGER_AND_ATTY_RESPONSE
  },
};

// ============================================================================
// PERMISSION OBJECT - Combine all role configurations by platform
// ============================================================================

/**
 * Main permission configuration object.
 * Maps platform and role combinations to their specific permissions.
 * Permissions are organized by platform (XTND, LYDECKER) and then by user role.
 */
export const PERMISSION_FEATURES: Record<
  Platform,
  Partial<Record<UserRole, PermissionsConfig>>
> = {
  [Platform.XTND]: {
    [UserRole.ADMIN]: (ROLE_CONFIGS[UserRole.ADMIN] as AdminPlatformPermissions)?.XTND,
    [UserRole.CLERK]: ROLE_CONFIGS[UserRole.CLERK] as PermissionsConfig,
    [UserRole.BILLING_ADMIN]: ROLE_CONFIGS[UserRole.BILLING_ADMIN] as PermissionsConfig,
    [UserRole.BOOKKEEPER]: ROLE_CONFIGS[UserRole.BOOKKEEPER] as PermissionsConfig,
    [UserRole.ACCOUNTING_ADMIN]: ROLE_CONFIGS[UserRole.ACCOUNTING_ADMIN] as PermissionsConfig,
    [UserRole.SUPERVISOR]: ROLE_CONFIGS[UserRole.SUPERVISOR] as PermissionsConfig,
    [UserRole.HR_ADMIN]: ROLE_CONFIGS[UserRole.HR_ADMIN] as PermissionsConfig,
    [UserRole.HR_STANDARD]: ROLE_CONFIGS[UserRole.HR_STANDARD] as PermissionsConfig,
    [UserRole.PARTNER]: ROLE_CONFIGS[UserRole.PARTNER] as PermissionsConfig,
    [UserRole.ASSOCIATE]: ROLE_CONFIGS[UserRole.ASSOCIATE] as PermissionsConfig,
    [UserRole.ASSISTANT]: ROLE_CONFIGS[UserRole.ASSISTANT] as PermissionsConfig,
  },

  [Platform.LYDECKER]: {
    [UserRole.ADMIN]: (ROLE_CONFIGS[UserRole.ADMIN] as AdminPlatformPermissions)?.LYDECKER,
    [UserRole.CLERK]: ROLE_CONFIGS[UserRole.CLERK] as PermissionsConfig,
    [UserRole.BILLING_ADMIN]: ROLE_CONFIGS[UserRole.BILLING_ADMIN] as PermissionsConfig,
    [UserRole.BOOKKEEPER]: ROLE_CONFIGS[UserRole.BOOKKEEPER] as PermissionsConfig,
    [UserRole.ACCOUNTING_ADMIN]: ROLE_CONFIGS[UserRole.ACCOUNTING_ADMIN] as PermissionsConfig,
    [UserRole.SUPERVISOR]: ROLE_CONFIGS[UserRole.SUPERVISOR] as PermissionsConfig,
    [UserRole.HR_ADMIN]: ROLE_CONFIGS[UserRole.HR_ADMIN] as PermissionsConfig,
    [UserRole.HR_STANDARD]: ROLE_CONFIGS[UserRole.HR_STANDARD] as PermissionsConfig,
    [UserRole.PARTNER]: ROLE_CONFIGS[UserRole.PARTNER] as PermissionsConfig,
    [UserRole.ASSOCIATE]: ROLE_CONFIGS[UserRole.ASSOCIATE] as PermissionsConfig,
    [UserRole.ASSISTANT]: ROLE_CONFIGS[UserRole.ASSISTANT] as PermissionsConfig,
  }
};

